export interface IWsPing {
  channel: 'ping';
  time: number;
}

export interface ILastCycleMessage {
  channel: 'last_cycle';
  bot_id: number;
  cycle: {
    id: number;
    status: number;
    currency: string;
    currency_to: string;
  };
}

export type WsLastCycleMessage = IWsPing | ILastCycleMessage;

function isObject(v: unknown): v is Record<string, unknown> {
    return typeof v === 'object' && v !== null;
}

function isWsPing(v: unknown): v is IWsPing {
    return isObject(v) && v.channel === 'ping' && typeof v.time === 'number';
}

function isLastCycleMessage(v: unknown): v is ILastCycleMessage {
    if (!isObject(v)) return false;
    if (v.channel !== 'last_cycle') return false;
    if (typeof v.bot_id !== 'number') return false;

    const cycle = v.cycle;
    return (
        isObject(cycle) &&
    typeof cycle.id === 'number' &&
    typeof cycle.status === 'number' &&
    typeof cycle.currency === 'string' &&
    typeof cycle.currency_to === 'string'
    );
}

function isWsLastCycleMessage(v: unknown): v is WsLastCycleMessage {
    return isWsPing(v) || isLastCycleMessage(v);
}

class WsLastCycle {
  private socket: WebSocket | null = null;
  private url: string;

  private readonly listeners: Set<(data: WsLastCycleMessage) => void> = new Set();

  private reconnectTimeout: number | null = null;
  private shouldReconnect = true;
  private generation = 0;

  private lastPingAt: number | null = null;

  constructor(sessToken: string, botId: number | string) {
      if (!sessToken) throw new Error('sessToken is empty');
      if (botId === null || botId === undefined || String(botId) === '') {
          throw new Error('botId is empty');
      }

      const sess = encodeURIComponent(sessToken);
      const id = encodeURIComponent(String(botId));

      this.url = `wss://ws.revenuebot.io/AuthWsOrders?sess=${sess}&action=last_cycle&bot_id=${id}`;
  }

  connect(): void {
      if (
          this.socket &&
      (this.socket.readyState === WebSocket.CONNECTING ||
        this.socket.readyState === WebSocket.OPEN)
      ) {
          console.warn('WebSocket already connecting/open.');
          return;
      }

      this.shouldReconnect = true;

      this.generation += 1;
      const gen = this.generation;

      if (this.reconnectTimeout !== null) {
          clearTimeout(this.reconnectTimeout);
          this.reconnectTimeout = null;
      }

      const socket = new WebSocket(this.url);
      this.socket = socket;

      socket.onopen = () => {
          if (gen !== this.generation) return;

          if (this.reconnectTimeout !== null) {
              clearTimeout(this.reconnectTimeout);
              this.reconnectTimeout = null;
          }
      };

      socket.onmessage = (event) => {
          if (gen !== this.generation) return;

          let parsed: unknown;
          try {
              // event.data в DOM может быть не только string, поэтому приводим к string
              parsed = JSON.parse(event.data as string) as unknown;
          } catch (e) {
              console.error('WebSocket message JSON parse error:', e, event.data);
              return;
          }

          if (!isWsLastCycleMessage(parsed)) {
              console.warn('WS message has unexpected shape:', parsed);
              return;
          }

          // фикс для сборок/TS, где сужение в колбэках иногда "не доезжает"
          const msg: WsLastCycleMessage = parsed;

          if (msg.channel === 'ping') {
              this.lastPingAt = msg.time;
          }

          this.listeners.forEach((callback) => callback(msg));
      };

      socket.onclose = (event) => {
          const rs = socket.readyState;

          console.warn('WS closed', {
              code: event.code,
              reason: event.reason,
              wasClean: event.wasClean,
              url: this.url,
              readyState: rs,
              closedWhileConnecting: rs === WebSocket.CONNECTING,
          });

          if (gen !== this.generation) return;

          if (this.socket === socket) {
              this.socket = null;
          }

          if (this.shouldReconnect) {
              this.reconnect(gen);
          }
      };

      socket.onerror = (error) => {
          if (gen !== this.generation) return;
          console.error('WebSocket error:', error);
      };
  }

  disconnect(): void {
      this.shouldReconnect = false;
      this.generation += 1;

      if (this.reconnectTimeout !== null) {
          clearTimeout(this.reconnectTimeout);
          this.reconnectTimeout = null;
      }

      if (this.socket) {
          this.socket.close(1000, 'client disconnect');
          this.socket = null;
      }
  }

  private reconnect(genAtSchedule: number): void {
      if (this.reconnectTimeout !== null || !this.shouldReconnect) return;

      console.log('Attempting to reconnect in 5 seconds...');
      this.reconnectTimeout = window.setTimeout(() => {
          this.reconnectTimeout = null;

          if (!this.shouldReconnect || this.generation !== genAtSchedule) return;

          this.connect();
      }, 5000);
  }

  sendMessage(message: unknown): void {
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
          this.socket.send(JSON.stringify(message));
      } else {
          console.warn('WebSocket is not connected.');
      }
  }

  isConnected(): boolean {
      return this.socket !== null && this.socket.readyState === WebSocket.OPEN;
  }

  isConnecting(): boolean {
      return this.socket !== null && this.socket.readyState === WebSocket.CONNECTING;
  }

  getReadyState(): number | null {
      return this.socket ? this.socket.readyState : null;
  }

  getLastPingAt(): number | null {
      return this.lastPingAt;
  }

  addListener(callback: (data: WsLastCycleMessage) => void): void {
      this.listeners.add(callback);
  }

  removeListener(callback: (data: WsLastCycleMessage) => void): void {
      this.listeners.delete(callback);
  }

  clearListeners(): void {
      this.listeners.clear();
  }
}

export { WsLastCycle };
export default WsLastCycle;